import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Grid, Icon, IconButton, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { InfoBlock } from '../InfoBlock';
import { useViewedVehicle } from '../../hooks/useViewedVehicle';
import { useUnmarkVehicleAsVisibleMutation } from 'services/api/admin-auction-vehicles';
import { convertToLocalTime } from 'utils';
import { VisibleBuyer } from 'types/vehicle';
import { useIsAdmin } from 'hooks/selectors';

const BidHistoryWrapper = styled.div`
  padding: 21px 25px 21px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitleText = styled(Typography)`
  font-size: 16px;
  font-weight: 600;
  color: #212121;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledText = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  color: #666666;
  font-size: 14px;
`;

const RowList = styled.div`
  max-height: 140px;
  overflow-y: scroll;
`;

const ModalRowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 400px;
  overflow-y: auto;
`;

const VisibleBuyerRow = ({ item, onDelete }: { item: VisibleBuyer; onDelete?: (item: VisibleBuyer) => void }) => (
  <Grid container alignItems="center" style={{ gap: '8px', flexWrap: 'nowrap' }}>
    <Grid item>
      <StyledText>-</StyledText>
    </Grid>
    <Grid item container wrap="nowrap" alignItems="center" style={{ flex: '1 1 300%' }}>
      <StyledText style={{ lineHeight: 'normal', maxWidth: 'unset' }} title={item.user?.dealership_name}>
        {`by ${item.user?.dealership_name} (${item.creator?.first_name})`}
      </StyledText>
    </Grid>
    <Grid item container style={{ justifyContent: 'flex-end', flex: '1 1 300%' }}>
      <StyledText style={{ lineHeight: 'normal' }}>{item.created_at ? convertToLocalTime(item.created_at, true) : ''}</StyledText>
    </Grid>
    {onDelete && (
      <Grid item>
        <IconButton onClick={() => onDelete(item)} style={{ padding: 4 }}>
          <Icon style={{ fontSize: 18 }}>delete</Icon>
        </IconButton>
      </Grid>
    )}
  </Grid>
);

export const VisibleBuyersBlock = () => {
  const vehicle = useViewedVehicle();
  const [unmarkVisible] = useUnmarkVehicleAsVisibleMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMainAdmin = useIsAdmin();

  const visibleBuyers = vehicle?.adminVisibleBuyers ?? [];

  if (visibleBuyers.length === 0) return null;

  const handleDelete = (item: VisibleBuyer) => {
    if (!window.confirm(`Are you sure you want to remove ${item.user?.dealership_name}'s visibility?`)) return;
    unmarkVisible({ vehicleId: vehicle!.id, userIds: [item.user_id] });
  };

  return (
    <InfoBlock>
      <BidHistoryWrapper>
        <HeaderRow>
          <SectionTitleText>{`Visible on My Bids (${visibleBuyers.length})`}</SectionTitleText>
          <Button
            style={{ textTransform: 'none', color: '#3994DE' }}
            size="small"
            color="primary"
            onClick={() => setIsModalOpen(true)}
          >
            View All
          </Button>
        </HeaderRow>
        <RowList>
          <Grid container direction="column">
            {visibleBuyers.map((item, index) => (
              <VisibleBuyerRow key={index} item={item} />
            ))}
          </Grid>
        </RowList>
      </BidHistoryWrapper>

      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{`Visible on My Bids (${visibleBuyers.length})`}</span>
            <IconButton onClick={() => setIsModalOpen(false)} style={{ padding: 4 }}>
              <Icon>close</Icon>
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <ModalRowList>
            {visibleBuyers.map((item, index) => (
              <VisibleBuyerRow key={index} item={item} onDelete={isMainAdmin ? handleDelete : undefined} />
            ))}
          </ModalRowList>
        </DialogContent>
      </Dialog>
    </InfoBlock>
  );
};
